import { useState, useEffect } from 'react';
import type { UserWithPermissions, TableInfo, PermissionState } from '@/types/admin';
import {
  updateUserProfile,
  upsertUserPermissions,
  createUser,
  permissionsToState,
} from '@/services/admin';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { PermissionManager } from './PermissionManager';
import { X, Check, AlertCircle } from 'lucide-react';

interface UserEditModalProps {
  user: UserWithPermissions | null;
  tables: TableInfo[];
  onClose: () => void;
  onSave: () => void;
}

export function UserEditModal({ user, tables, onClose, onSave }: UserEditModalProps) {
  const isNewUser = !user;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<{
    email: string;
    password: string;
    fullName: string;
    role: string;
  }>({
    email: user?.email || '',
    password: '',
    fullName: user?.full_name || '',
    role: user?.role || 'user',
  });

  // Permissions state
  const [permissions, setPermissions] = useState<PermissionState>({});

  // Load existing permissions if editing
  useEffect(() => {
    if (user?.permissions) {
      setPermissions(permissionsToState(user.permissions));
    }
  }, [user]);

  // Handle form changes
  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Handle save
  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      if (user) {
        // Update existing user
        await updateUserProfile(user.id, {
          fullName: formData.fullName,
          role: formData.role as 'admin' | 'manager' | 'user',
        });
        await upsertUserPermissions(user.id, permissions);
      } else {
        // Create new user
        if (!formData.email || !formData.password) {
          throw new Error('Email and password are required');
        }
        const newUser = await createUser(
          formData.email,
          formData.password,
          formData.fullName,
          formData.role as 'admin' | 'manager' | 'user'
        );
        // Save permissions for the new user
        await upsertUserPermissions(newUser.id, permissions);
      }
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card rounded-xl w-[95vw] max-w-lg md:max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {isNewUser ? 'Add New User' : 'Edit User'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {/* User Details */}
            <div className="space-y-4">
              <h3 className="font-medium">User Details</h3>

              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  placeholder="user@example.com"
                  disabled={!isNewUser}
                />
              </div>

              {isNewUser && (
                <div>
                  <label className="block text-sm font-medium mb-1">Password</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleFormChange('password', e.target.value)}
                    placeholder="Enter password"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Full Name</label>
                <Input
                  value={formData.fullName}
                  onChange={(e) => handleFormChange('fullName', e.target.value)}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-background"
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Role Info */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">Role Permissions:</p>
                <div className="space-y-1 text-sm">
                  <p><Badge variant="default">User</Badge> - Limited access based on table permissions</p>
                  <p><Badge variant="warning">Manager</Badge> - Can manage assigned tables</p>
                  <p><Badge variant="destructive">Admin</Badge> - Full access to all tables</p>
                </div>
              </div>
            </div>

            {/* Table Permissions */}
            <div className="lg:col-span-2">
              <h3 className="font-medium mb-4">Table Permissions</h3>
              <PermissionManager
                permissions={permissions}
                tables={tables}
                onChange={setPermissions}
                disabled={formData.role === 'admin'}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-muted/30">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {isNewUser ? 'Cancel' : 'Close'}
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {isNewUser ? 'Create User' : 'Save Changes'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
